import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Method,
    Prop,
    State,
    Watch,
} from '@stencil/core';
import { ThemeType } from '@uipath/portal-shell-util';

import store from '../../store/store';
import Logger from '../../utils/logger/logger';
import { getTheme } from '../../utils/themeHelper';

@Component({
    tag: 'portal-menu-surface',
    styleUrl: 'portal-menu-surface.scss',
    shadow: true,
})
export class PortalMenuSurface {
    /**
     * Triggered when the menu surface is opened
     * @event PortalMenuSurface
     */
    @Event({
        eventName: 'portalMenuSurfaceOpened',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) portalMenuSurfaceOpened: EventEmitter<void>;

    /**
     * Triggered when the menu surface is opened
     * @event PortalMenuSurface
     */
    @Event({
        eventName: 'portalMenuSurfaceClosed',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) portalMenuSurfaceClosed: EventEmitter<void>;

    /**
    * Theme override to bypass store state
    * @memberof PortalMenuSurface
    */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
    * sets top of the modal css
    * @memberof PortalMenuSurface
    */
    @Prop() top: string = '0';

    /**
    * sets left of the modal css
    * @memberof PortalMenuSurface
    */
    @Prop() left: string;

    /**
     * sets right of the modal css
     * @memberof PortalMenuSurface
     */
    @Prop() right: string = '';

    /**
    * centers the surface element
    * @memberof PortalMenuSurface
    */
    @Prop() center: boolean = false;

    /**
     * replace default opacity transition
     * with custom slide in & out transition
     */
    @Prop() slideTransition: boolean = false;

    /**
     * add a darkening background effect
     * when opening the surface menu
     */
    @Prop() backgroundEffect: boolean = false;

    /**
     * The aria-role for the surface modal
     * @memberof PortalMenuLink
     */
    @Prop() containerRole?: string;

    /**
     * Prevent closing the menu surface when clicking outside of it or pressing escape
     * @memberof PortalMenuSurface
     * @default false
     * @type {boolean}
     */
    @Prop() preventClose: boolean = false;

    @Element() element: HTMLPortalMenuSurfaceElement;

    @State() open: boolean = false;

    private modalElement!: HTMLElement;

    @Watch('open')
    onOpenedChange(newValue: boolean | undefined) {
        if (newValue === true) {
            this.portalMenuSurfaceOpened.emit();
        } else if (newValue === false) {
            this.portalMenuSurfaceClosed.emit();
        }
    }

    /**
    * get open state of the surface menu
    * @memberof PortalMenuSurface
    */
    @Method()
    async isOpen() {
        return this.open;
    }

    /**
    * sets menu surface visible to true
    * @method PortalMenuSurface
    */
    @Method()
    async openMenu(elem?: HTMLElement) {
        this.open = true;
        if (elem) {
            this.handleMenuPositionOverflow(elem);
        }

        return Promise.resolve();
    }

    /**
     * toggle the surface visibility
     * @method PortalMenuSurface
     */
    @Method()
    async toggleMenu(elem?: HTMLElement) {
        this.open = !this.open;
        if (this.open && elem) {
            this.handleMenuPositionOverflow(elem);
        }

        return Promise.resolve();
    }

    /**
    * sets menu surface visible to false
    * @method PortalMenuSurface
    */
    @Method()
    async closeMenu() {
        this.open = false;
        return Promise.resolve();
    }

    render() {
        return (<Host class={getTheme(store, this.theme)}>
            <div id="wrapper-root"
                data-test="portal-menu-surface"
                class={this.getRootClass()}
                onClick={(ev) => {
                    if (!this.preventClose && ev.target === ev.currentTarget) {
                        this.closeMenu();
                    }
                }}>
                <div class={`portal-modal ${this.slideTransition ? (this.open ? 'slide-in' : 'slide-out') : ''} ${this.center ? 'centered-menu' : ''}`}
                    data-test="portal-modal"
                    style={{
                        top: this.top,
                        left: this.left,
                        right: this.right,
                    }}
                    {...(this.containerRole ? { role: this.containerRole } : {})}
                    onKeyPress={(ev) => this.handleEscapeKeyPress(ev)}
                    ref={(el) => this.modalElement = el as HTMLElement}>
                    <slot></slot>
                </div>
            </div>
        </Host>
        );
    }

    private getRootClass() {
        let rootClass = `portal-surface-wrapper ${this.slideTransition ? 'visibility-transition' : 'opacity-transition'}`;

        if (this.open) {
            rootClass += ' visible';
        }

        if (this.backgroundEffect) {
            rootClass += ' background-effect';
        }

        return rootClass;
    }

    private handleEscapeKeyPress(e: KeyboardEvent) {
        if (!this.preventClose && e.key === 'Escape') {
            Logger.debug('portal-menu-surface', 'escape key pressed');
            this.closeMenu();
        }
    }

    /**
    * Sets menu surface position to stay within viewport
    * @method handleMenuPositionOverflow
    */
    private handleMenuPositionOverflow(elem: HTMLElement) {
        const menuBoundingRect = this.modalElement.getBoundingClientRect();
        const clickedElemBoundingRect = elem.getBoundingClientRect();

        /**
        * Handle menu overflow right
        * If it is larger than the width of the viewport, open starting from the left side of the viewport
        * If it goes beyond the viewport, open it to the left side of the clicked element
        */
        if (window.innerWidth < menuBoundingRect.width) {
            this.modalElement.style.left = '0px';
        } else if (window.innerWidth < clickedElemBoundingRect.right + menuBoundingRect.width) {
            this.modalElement.style.left = (clickedElemBoundingRect.right - menuBoundingRect.width) + 'px';
        } else {
            this.modalElement.style.left = (clickedElemBoundingRect.left) + 'px';
        }

        /**
         * Handle menu overflow bottom
         * if it is larger than the height of the viewport, open starting from the top of the viewport and fill the height of the viewport
         * If it goes beyond the viewport, open it above the clicked element
         */
        if (window.innerHeight < menuBoundingRect.height + clickedElemBoundingRect.height) {
            this.modalElement.style.top = '0px';
            this.modalElement.style.maxHeight = window.innerHeight + 'px';
            this.modalElement.style.overflowY = 'auto';
        } else if (window.innerHeight < clickedElemBoundingRect.bottom + menuBoundingRect.height) {
            this.modalElement.style.top = (clickedElemBoundingRect.top - menuBoundingRect.height) + 'px';
            this.modalElement.style.maxHeight = 'unset';
        } else {
            this.modalElement.style.top = (clickedElemBoundingRect.top + clickedElemBoundingRect.height) + 'px';
            this.modalElement.style.maxHeight = 'unset';
        }

    }
}
